import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { AssistantStreamEvent } from "@opus-dt/shared-types";
import type { StreamPhase } from "@/types/assistant.types";

// Assina o SSE do turno (espelha SimulationProgress): acumula a narração e
// os cards que chegam ao vivo; no evento terminal fecha, invalida a conversa e chama onTerminal.
// Religa por turnId — quando a conversa carrega com um turno QUEUED/RUNNING, o dock passa esse id
// aqui e a rota faz replay do terminal se ele já encerrou (retomada pós-F5).

interface UseAssistantStreamParams {
  turnId: string | null;
  conversationId: string | null;
  // Chamado no evento terminal com o tipo e, em `error`, a mensagem amigável (o turno FAILED não
  // gera bolha do assistente ⇒ o dock guarda a mensagem para exibir).
  onTerminal?: (type: "done" | "error" | "canceled", message?: string) => void;
}

const TERMINAL = new Set(["done", "error", "canceled"]);

export function useAssistantStream({
  turnId,
  conversationId,
  onTerminal,
}: UseAssistantStreamParams): { events: AssistantStreamEvent[]; phase: StreamPhase } {
  const [events, setEvents] = useState<AssistantStreamEvent[]>([]);
  const [phase, setPhase] = useState<StreamPhase>("idle");
  const [subscribedTurnId, setSubscribedTurnId] = useState<string | null>(null);
  const qc = useQueryClient();

  // Reset ao trocar de turno — padrão React de ajustar estado durante o render (não em effect):
  // https://react.dev/reference/react/useState#storing-information-from-previous-renders
  if (turnId !== subscribedTurnId) {
    setSubscribedTurnId(turnId);
    setEvents([]);
    setPhase(turnId ? "streaming" : "idle");
  }

  useEffect(() => {
    if (!turnId) return;
    const src = new EventSource(`/api/v1/assistant/turns/${turnId}/stream`, {
      withCredentials: true,
    });

    src.onmessage = (e) => {
      let evt: AssistantStreamEvent;
      try {
        evt = JSON.parse(e.data as string) as AssistantStreamEvent;
      } catch {
        return; // evento ilegível: ignora (o replay pós-terminal reconstrói do banco)
      }
      setEvents((prev) => [...prev, evt]);
      if (TERMINAL.has(evt.type)) {
        src.close();
        setPhase("idle");
        if (conversationId) {
          void qc.invalidateQueries({ queryKey: ["assistant", "conversation", conversationId] });
          void qc.invalidateQueries({ queryKey: ["assistant", "conversations"] });
        }
        onTerminal?.(
          evt.type as "done" | "error" | "canceled",
          evt.type === "error" ? evt.message : undefined
        );
      }
    };
    // Erro de conexão: EventSource re-tenta sozinho; o terminal replay fecha limpo na volta.
    src.onerror = () => undefined;

    return () => src.close();
    // onTerminal é estável (useCallback no dock); conversationId/turnId disparam a religação.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [turnId, conversationId]);

  return { events, phase };
}
